import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, X, ArrowRight, Rocket } from "lucide-react";

export function OnboardingChecklist({ variant = "card" }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = () => api.get("/firm/onboarding").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data || hidden) return null;
  // On the dashboard card, auto-hide once dismissed or fully complete.
  if (variant === "card" && (data.dismissed || data.all_done)) return null;

  const dismiss = async () => {
    setHidden(true);
    try { await api.post("/firm/onboarding/dismiss"); } catch { /* non-blocking */ }
  };

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <div
      className="border border-[#0B1F33] bg-white mb-6"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-zinc-100">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center bg-[#0B1F33] text-[#D5A44A]">
            <Rocket className="h-4 w-4" />
          </span>
          <div>
            <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#B54747]">Getting started</p>
            <h2 className="font-display text-xl leading-tight">
              {data.all_done ? "You're all set up" : "Set up your firm in a few quick steps"}
            </h2>
            <p className="text-xs text-zinc-500 mt-1" data-testid="onboarding-progress">
              {data.completed} of {data.total} complete
            </p>
          </div>
        </div>
        {variant === "card" && (
          <button
            type="button"
            onClick={dismiss}
            data-testid="onboarding-dismiss"
            className="text-zinc-400 hover:text-zinc-700 transition-colors"
            aria-label="Dismiss checklist"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* progress bar */}
      <div className="h-1 bg-zinc-100">
        <div className="h-1 bg-[#156F52] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      <ul className="divide-y divide-zinc-100">
        {data.steps.map((s) => (
          <li key={s.key} className="flex items-center justify-between px-6 py-3" data-testid={`onboarding-step-${s.key}`}>
            <div className="flex items-center gap-3">
              {s.done ? (
                <CheckCircle2 className="h-5 w-5 text-[#156F52] shrink-0" data-testid={`onboarding-step-${s.key}-done`} />
              ) : (
                <Circle className="h-5 w-5 text-zinc-300 shrink-0" />
              )}
              <span className={`text-sm ${s.done ? "text-zinc-400 line-through" : "text-zinc-800"}`}>{s.label}</span>
            </div>
            {!s.done && (
              <Link
                to={s.path}
                data-testid={`onboarding-step-${s.key}-cta`}
                className="inline-flex items-center gap-1 text-xs font-medium text-[#0B1F33] hover:text-[#D5A44A] transition-colors group"
              >
                Go <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default OnboardingChecklist;
